import { graphql } from 'gatsby'
import React, { useEffect, useMemo } from 'react'

import { SEO } from '../components/SEO'
import { TypeNames } from '../constants/enums'
import { useLocale } from '../context/LocaleContextProvider'
import { isPageContextFor, usePageContext } from '../context/PageContextProvider'
import { IBlogCatalog } from '../graphql/modules/blog'
import { IBlogCatalogSection } from '../graphql/sections/blogCatalogCategories'
import { IMetadata } from '../graphql/sections/metadata'
import { Sections } from '../graphql/sections/types'
import deriveMetadata from '../helpers/deriveMetadata'
import { useSendPageViewedEvent } from '../hooks/analytics/useSendPageViewedEvent'
import { useLiveContentfulData } from '../hooks/useLiveContentfulData'
import { SectionContainer } from '../styles/containerStyles'
import { BlogCatalogWithAuthorSection } from './BlogCatalogWithAuthorSection'
import { BlogCatalogWithCategoriesSection } from './BlogCatalogWithCategoriesSection'
import FooterSection from './FooterSection'
import StickyCTASection from './StickyCTASection'

type Reduces<T, U extends T> = U

interface IProps {
  data: {
    contentfulPage: {
      sections: Reduces<Sections, IMetadata | IBlogCatalogSection>[]
      contentful_id: string
    }
    allContentfulBlog: {
      edges: {
        node: IBlogCatalog
      }[]
    }
  }
}

const PageAdapterBlog: React.FC<IProps> = ({ data }) => {
  const {
    contentfulPage: { sections: sectionsList, contentful_id: pageContentfulId },
    allContentfulBlog: { edges: allContentfulBlogEdges },
  } = useLiveContentfulData(data, data.contentfulPage.contentful_id)

  const { pageContext, setPageContext } = usePageContext()
  // A boolean, not pageContext: the object changes whenever any field is set again (the experiment treatment,
  // for one), and each change would send another Page Viewed for the same page.
  const isCurrentPageContext = isPageContextFor(pageContext, pageContentfulId)
  const sendPageViewedEvent = useSendPageViewedEvent()
  const { t } = useLocale()

  useEffect(() => {
    setPageContext({
      pageContentfulId,
      // Experiments are not supported yet for blog pages.
      experiment: null,
      treatmentRequested: null,
      treatmentSelected: null,
      pagePath: window.location.pathname,
      pageUrl: window.location.href,
    })
  }, [pageContentfulId])

  useEffect(() => {
    if (isCurrentPageContext) {
      sendPageViewedEvent(pageContentfulId)
    }
  }, [pageContentfulId, isCurrentPageContext, sendPageViewedEvent])

  const allBlogs = React.useMemo(() => {
    return allContentfulBlogEdges.map(({ node }) => node)
  }, [allContentfulBlogEdges])

  const hasMetadata = sectionsList.some(section => section.__typename === TypeNames.CONTENTFUL_METADATA)

  if (!hasMetadata) {
    sectionsList.push(deriveMetadata(sectionsList, t))
  }

  const sections = useMemo(
    () =>
      sectionsList &&
      sectionsList.map(section => {
        switch (section.__typename) {
          case TypeNames.CONTENTFUL_BLOG_CATALOG_WITH_CATEGORIES: {
            return section.authorBio ? (
              <BlogCatalogWithAuthorSection key={section.contentful_id} data={section} allBlogs={allBlogs} />
            ) : (
              <BlogCatalogWithCategoriesSection key={section.contentful_id} data={section} allBlogs={allBlogs} />
            )
          }
          case TypeNames.CONTENTFUL_METADATA: {
            return <SEO key={section.contentful_id} data={section} />
          }
          default: {
            return null
          }
        }
      }),
    [sectionsList, allBlogs],
  )

  return (
    <SectionContainer data-entry-id={pageContentfulId}>
      {sections}
      <StickyCTASection />
      <FooterSection />
    </SectionContainer>
  )
}

export default PageAdapterBlog

export const pageQuery = graphql`
  query ContentfulBlogPage($pageId: String!, $nodeLocale: String!) {
    contentfulPage(contentful_id: { eq: $pageId }, node_locale: { eq: $nodeLocale }) {
      contentful_id
      sections {
        __typename
        ...MetadataFragment
        ...BlogCatalogWithCategoriesFragment
      }
    }
    allContentfulBlog(filter: { node_locale: { eq: $nodeLocale } }, sort: { fields: date, order: DESC }) {
      edges {
        node {
          ...BlogCatalogFragment
        }
      }
    }
  }
`
